import axios from 'axios'

import { APP_API_URL } from './api-config.service'


type ClientErrorSource = 'window.error' | 'unhandledrejection' | 'react.error-boundary' | 'resource.error' | 'api.error' | 'caught.error'

type ClientErrorDetails = {
    componentStack?: string | null
    fileName?: string
    line?: number
    column?: number
    context?: string
}

type ClientErrorReport = {
    clientError: {
        source: ClientErrorSource
        message: string
        stack: string
        componentStack: string
        fileName: string
        line: number
        column: number
        path: string
        context: string
        droppedCount: number
    }
}

type QueuedClientError = {
    report: ClientErrorReport
    isSending: boolean
}

const REPORT_WINDOW_MS = 60 * 1000
const DUPLICATE_TTL_MS = 60 * 1000
const MAX_REPORTS_PER_WINDOW = 10
const MAX_PENDING_REPORTS = 10
const REPORT_TIMEOUT_MS = 5000
const reportTimestamps: number[] = []
const recentFingerprints = new Map<string, number>()
const pendingReports: QueuedClientError[] = []
let areGlobalHandlersInstalled = false
let isCreatingReport = false
let droppedReportCount = 0

const normalizeText = (value: unknown, maxLength: number) => {
    try {
        return String(value ?? '')
            .replace(/[\0\r\n]+/g, ' ')
            .replace(/(\/(?:infoContract|canceledContract)\/)[^/?#\s'"]+/gi, '$1[REDACTED]')
            .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[EMAIL_REDACTED]')
            .replace(/\+?\d(?:[\s().-]*\d){9,}/g, '[PHONE_REDACTED]')
            .trim()
            .slice(0, maxLength)
    } catch {
        return ''
    }
}

const getAuthToken = () => {
    try {
        return localStorage.getItem('us_id')
    } catch {
        return null
    }
}

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message || error.name
    if (typeof error === 'string' || typeof error === 'number' || typeof error === 'boolean') return String(error)

    return 'Необработанная ошибка интерфейса.'
}

const getSafeFileName = (fileName: string) => {
    try {
        const fileUrl = new URL(fileName, window.location.origin)
        return fileUrl.origin === window.location.origin ? fileUrl.pathname : ''
    } catch {
        return ''
    }
}

const getSafePagePath = () => normalizeText(window.location.pathname, 500)

const getSafeRequestPath = (requestUrl?: string) => {
    try {
        return normalizeText(new URL(requestUrl || '/', window.location.origin).pathname, 500)
    } catch {
        return '/unknown'
    }
}

const canSendReport = (message: string, stack: string) => {
    const now = Date.now()

    while (reportTimestamps.length > 0 && now - reportTimestamps[0] >= REPORT_WINDOW_MS) {
        reportTimestamps.shift()
    }
    for (const [fingerprint, timestamp] of recentFingerprints) {
        if (now - timestamp >= DUPLICATE_TTL_MS) recentFingerprints.delete(fingerprint)
    }

    const fingerprint = `${message}|${stack.slice(0, 1000)}`
    if (reportTimestamps.length >= MAX_REPORTS_PER_WINDOW || recentFingerprints.has(fingerprint)) return false

    reportTimestamps.push(now)
    recentFingerprints.set(fingerprint, now)
    return true
}

const removePendingReport = (queuedReport: QueuedClientError) => {
    const reportIndex = pendingReports.indexOf(queuedReport)
    if (reportIndex >= 0) pendingReports.splice(reportIndex, 1)
}

const sendPendingReport = async (queuedReport: QueuedClientError, token: string) => {
    queuedReport.isSending = true
    const abortController = new AbortController()
    const timeoutID = setTimeout(() => abortController.abort(), REPORT_TIMEOUT_MS)

    try {
        const response = await fetch(`${APP_API_URL}/common/reportClientError`, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            signal: abortController.signal,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(queuedReport.report),
        })

        if (response.ok || response.status === 400 || response.status === 403) {
            removePendingReport(queuedReport)
        }
    } catch {
        return
    } finally {
        clearTimeout(timeoutID)
        queuedReport.isSending = false
    }
}

export const flushPendingClientErrors = () => {
    try {
        const token = getAuthToken()
        if (!token) return

        pendingReports.forEach((queuedReport) => {
            if (!queuedReport.isSending) void sendPendingReport(queuedReport, token)
        })
    } catch {
        return
    }
}

export const reportClientError = (source: ClientErrorSource, error: unknown, details: ClientErrorDetails = {}) => {
    if (isCreatingReport) return
    isCreatingReport = true

    try {
        const message = normalizeText(getErrorMessage(error), 1000)
        const stack = normalizeText(error instanceof Error ? error.stack : '', 8000)
        if (!message || !canSendReport(message, stack)) {
            droppedReportCount += 1
            return
        }

        const report: ClientErrorReport = {
            clientError: {
                source,
                message,
                stack,
                componentStack: normalizeText(details.componentStack, 4000),
                fileName: normalizeText(getSafeFileName(details.fileName || ''), 500),
                line: Number.isSafeInteger(details.line) ? details.line! : 0,
                column: Number.isSafeInteger(details.column) ? details.column! : 0,
                path: getSafePagePath(),
                context: normalizeText(details.context, 1000),
                droppedCount: droppedReportCount,
            },
        }
        droppedReportCount = 0

        if (pendingReports.length >= MAX_PENDING_REPORTS) {
            pendingReports.shift()
            report.clientError.droppedCount += 1
        }
        pendingReports.push({ report, isSending: false })
        flushPendingClientErrors()
    } catch {
        droppedReportCount += 1
    } finally {
        isCreatingReport = false
    }
}

export const reportUnexpectedClientError = (error: unknown, context: string) => {
    reportClientError('caught.error', error, { context })
}

export const reportApiError = (error: unknown) => {
    if (axios.isCancel(error)) return
    if (!axios.isAxiosError(error)) {
        reportUnexpectedClientError(error, 'API request executor')
        return
    }

    const status = Number(error.response?.status)
    if (status > 0 && status < 500) return

    const method = String(error.config?.method || 'GET').toUpperCase()
    const requestPath = getSafeRequestPath(error.config?.url)
    const requestID = normalizeText(error.response?.headers?.['x-request-id'], 100)
    const result = status > 0 ? `HTTP ${status}` : normalizeText(error.code || 'NETWORK_ERROR', 100)
    reportClientError('api.error', error, {
        context: `${method} ${requestPath} → ${result}${requestID ? ` → request=${requestID}` : ''}`,
    })
}

export const installGlobalClientErrorHandlers = () => {
    if (areGlobalHandlersInstalled) return
    areGlobalHandlersInstalled = true

    window.addEventListener('error', (event) => {
        if (event instanceof ErrorEvent) {
            reportClientError('window.error', event.error || event.message, {
                fileName: event.filename,
                line: event.lineno,
                column: event.colno,
            })
            return
        }

        const target = event.target
        const resourceUrl = target instanceof HTMLScriptElement
            ? target.src
            : target instanceof HTMLLinkElement && target.rel === 'stylesheet'
                ? target.href
                : target instanceof HTMLAudioElement
                    ? target.src
                    : ''
        const safeFileName = resourceUrl ? getSafeFileName(resourceUrl) : ''
        if (!safeFileName) return

        reportClientError('resource.error', new Error('Не удалось загрузить ресурс интерфейса.'), {
            fileName: safeFileName,
        })
    }, true)

    window.addEventListener('unhandledrejection', (event) => {
        reportClientError('unhandledrejection', event.reason)
    })
}